/**
 * OAuth. Web Client
 *
 * This file implements the OpenId Connect client logic for interacting with the keycloak Apis.
 * It handles the essential auth. operations for Whitehelmet services.
 *
 * for more information please visit:
 * @link https://whitehelmet.atlassian.net/wiki/spaces/WHDMSWORKS/pages/785907713/Keycloak+Integration+with+Custom+APIs
 *
 * @author Mohamed Adel<madel@whitehelmet.sa>
 * @date 2025-07-07
 * @version 1.0.0
 * @copyright 2025 Whitehelmet. All rights reserved.
 */
import { HttpFetchClient } from "@whitehelmet/http-client";
import { ENDPOINTS } from "./constants.js";
export class OAuthClient {
    realm;
    clientId;
    grantType;
    httpClient;
    endpoints = { ...ENDPOINTS };
    constructor(
    /**
     * @param {string} origin - Origin Url
     * @example https://prod-keycloak.whitehelmet.sa
     */
    origin, 
    /**
     * @param {string} realm - KeyCloack realm
     * @example dev
     */
    realm, 
    /**
     * @param {string} clientId - client identifier
     * @example dms-frontend
     */
    clientId, 
    /**
     * @param {string} grantType - grant type
     * @default password
     */
    grantType = "password") {
        this.realm = realm;
        this.clientId = clientId;
        this.grantType = grantType;
        const httpClient = new HttpFetchClient();
        httpClient.setBaseUrl(origin);
        this.httpClient = httpClient;
    }
    /**
     * @description client secret key
     */
    clientSecret = "";
    /**
     * @description overrides default endpoints
     */
    overrideEndpoints(fn) {
        this.endpoints = fn();
    }
    /**
     * @description get current realm
     */
    getCurrentRealm() {
        return this.realm;
    }
    /**
     * @description set current realm
     */
    setCurrentRealm(realm) {
        this.realm = realm;
    }
    isOtpEnabled(status, data) {
        if (status !== 401)
            return false;
        if ("error" in data) {
            return data.error === "otp_required";
        }
        return false;
    }
    isOtpDisabled(status, data) {
        if (status !== 200)
            return false;
        return "access_token" in data;
    }
    setClientSecret(clientSecret) {
        this.clientSecret = clientSecret;
    }
    /**
     * @description request access token given a username and a password
     */
    async requestAccessTokenWithUsername(username, password) {
        const formParams = new URLSearchParams({
            username,
            password,
            client_id: this.clientId,
            grant_type: this.grantType,
        });
        const response = await this.httpClient.post(this.endpoints.ACCESS_TOKEN(this.realm), formParams.toString(), {
            "Content-Type": "application/x-www-form-urlencoded",
        });
        if (this.isOtpEnabled(response.status, response.data)) {
            return {
                success: true,
                otp: "enabled",
                data: response.data,
            };
        }
        if (this.isOtpDisabled(response.status, response.data)) {
            return {
                success: true,
                otp: "disabled",
                data: response.data,
            };
        }
        return {
            success: false,
            ...response.data,
        };
    }
    /**
     * @description request access token given an otp code
     */
    async requestAccessTokenWithOtp(email, otp) {
        const response = await this.httpClient.post(this.endpoints.OTP_ACCESS_TOKEN(this.realm), JSON.stringify({
            email,
            otp,
            client_id: this.clientId,
        }), {
            "Content-Type": "application/json",
        });
        return response.ok
            ? { success: true, ...response.data }
            : { success: false, ...response.data };
    }
    async verifyOtp(otp, email) {
        const response = await this.httpClient.post(this.endpoints.VERIFY_OTP(this.realm), JSON.stringify({
            otp,
            email,
        }), {
            "Content-Type": "application/json",
        });
        if (response.status === 200) {
            return {
                success: true,
                validOtp: true,
                data: {},
            };
        }
        else {
            return {
                success: false,
                validOtp: false,
                data: response.data,
            };
        }
    }
    async resendOtp(email) {
        const response = await this.httpClient.post(this.endpoints.RESEND_OTP(this.realm), JSON.stringify({
            email,
        }, null, 2), {
            "Content-Type": "application/json",
        });
        return response.ok
            ? { success: true, ...response.data }
            : { success: false, ...response.data };
    }
    async forgotPassword(email) {
        const response = await this.httpClient.post(this.endpoints.FORGOT_PASSWORD(this.realm), JSON.stringify({
            email,
        }, null, 2), {
            "Content-Type": "application/json",
        });
        return response.ok
            ? { success: true, ...response.data }
            : { success: false, ...response.data };
    }
    async resetPassword(email, otp, new_password) {
        const response = await this.httpClient.post(this.endpoints.RESET_PASSWORD(this.realm), JSON.stringify({
            email,
            otp,
            client_id: this.clientId,
            new_password,
        }, null, 2), {
            "Content-Type": "application/json",
        });
        return response.ok
            ? { success: true, ...response.data }
            : { success: false, ...response.data };
    }
    async changeLoggedInUserPassword(email, current_password, new_password, 
    // currently only this endpoint requires an access_token
    access_token) {
        const response = await this.httpClient.post(this.endpoints.CHANGE_PASSWORD(this.realm), JSON.stringify({
            email,
            old_password: current_password,
            new_password,
        }, null, 2), {
            "Authorization": "Bearer " + access_token,
            "Content-Type": "application/json",
        });
        return response.ok
            ? { success: true, ...response.data }
            : { success: false, ...response.data };
    }
    async revokeAccessToken(token) {
        const response = await this.httpClient.post(this.endpoints.REVOKE_ACCESS_TOKEN(this.realm), JSON.stringify({ token, client_id: this.clientId }), {
            "Content-Type": "application/json",
        });
        return response.ok
            ? { success: true, ...response.data }
            : { success: false, ...response.data };
    }
    async introspect(token) {
        const response = await this.httpClient.post(this.endpoints.INTROSPECT_ACCESS_TOKEN(this.realm), JSON.stringify({ token, client_id: this.clientId }), {
            "Content-Type": "application/json",
        });
        return response.ok
            ? { success: true, ...response.data }
            : {
                success: false,
                ...response.data,
            };
    }
}
